/**
 * Color Palettes Store - Pinia
 *
 * Manages color palettes for activity types and other colorable items.
 * Supports multiple palettes, color schemes, and user customization.
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';

export const useColorPalettesStore = defineStore('colorPalettes', {
  state: () => ({
    palettes: [],
    activePaletteId: null
  }),

  getters: {
    /**
     * Get the currently active palette
     * @returns {Object|null} The active palette or first available
     */
    getActivePalette: (state) => {
      return state.palettes.find(p => p.isActive) || state.palettes[0] || null;
    },

    /**
     * Get colors from the active palette
     * @returns {Array} Array of color objects { id, hex, name }
     */
    getActiveColors () {
      return this.getActivePalette?.colors || [];
    },

    /**
     * Get all palettes
     * @returns {Array} Array of all palettes
     */
    getAllPalettes: (state) => state.palettes,

    /**
     * Get a color by its ID from the active palette
     * @param {number} colorId - The color ID
     * @returns {Object|null} Color object { id, hex, name } or null
     */
    getColorById () {
      return (colorId) => {
        const colors = this.getActiveColors;
        return colors.find(c => c.id === colorId) || null;
      };
    },

    /**
     * Get hex value by color ID
     * @param {number} colorId - The color ID
     * @returns {string} Hex color string or default pink
     */
    getHexById () {
      return (colorId) => {
        const color = this.getColorById(colorId);
        return color?.hex || '#EC407A';
      };
    },

    /**
     * Get color ID by hex value (for migration/lookup)
     * @param {string} hex - The hex color string
     * @returns {number|null} Color ID or null
     */
    getColorIdByHex () {
      return (hex) => {
        const colors = this.getActiveColors;
        const color = colors.find(c => c.hex?.toUpperCase() === hex?.toUpperCase());
        return color?.id || null;
      };
    }
  },

  actions: {
    /**
     * Load all palettes from IndexedDB
     */
    async loadPalettes () {
      try {
        this.palettes = await GeoMoverDB.findAll('colorPalettes');
        const active = this.palettes.find(p => p.isActive);
        this.activePaletteId = active?.id || null;
        appDebugLog.log('[ColorPalettes] Loaded', this.palettes.length, 'palettes');
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error loading palettes:', error);
        this.palettes = [];
      }
    },

    /**
     * Set the active palette
     * @param {number} paletteId - ID of the palette to activate
     */
    async setActivePalette (paletteId) {
      try {
        // Deactivate all, activate selected
        for (const palette of this.palettes) {
          await GeoMoverDB.updateOne('colorPalettes', {
            isActive: palette.id === paletteId
          }, palette.id);
        }
        await this.loadPalettes();
        return true;
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error setting active palette:', error);
        return false;
      }
    },

    /**
     * Add a color to a palette
     * @param {number} paletteId - ID of the palette
     * @param {Object} color - Color object { hex, name }
     */
    async addColorToPalette (paletteId, color) {
      try {
        const palette = this.palettes.find(p => p.id === paletteId);
        if (!palette) return false;

        const newColor = {
          id: Math.max(...palette.colors.map(c => c.id), 0) + 1,
          hex: color.hex,
          name: color.name
        };

        await GeoMoverDB.updateOne('colorPalettes', {
          colors: [...palette.colors, newColor],
          updatedAt: Date.now()
        }, paletteId);

        await this.loadPalettes();
        return true;
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error adding color:', error);
        return false;
      }
    },

    /**
     * Remove a color from a palette
     * @param {number} paletteId - ID of the palette
     * @param {number} colorId - ID of the color to remove
     */
    async removeColorFromPalette (paletteId, colorId) {
      try {
        const palette = this.palettes.find(p => p.id === paletteId);
        if (!palette) return false;

        // Filter and convert to plain objects to avoid Vue proxy issues with IndexedDB
        const filteredColors = palette.colors
          .filter(c => c.id !== colorId)
          .map(c => ({ id: c.id, hex: c.hex, name: c.name }));

        await GeoMoverDB.updateOne('colorPalettes', {
          colors: filteredColors,
          updatedAt: Date.now()
        }, paletteId);

        await this.loadPalettes();
        return true;
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error removing color:', error);
        return false;
      }
    },

    /**
     * Create a new palette
     * @param {string} name - Name of the new palette
     * @param {Array} colors - Array of color objects
     */
    async createPalette (name, colors = []) {
      try {
        const newPalette = {
          name,
          isDefault: false,
          isActive: false,
          colors,
          createdAt: Date.now()
        };

        await GeoMoverDB.addOne('colorPalettes', newPalette);
        await this.loadPalettes();
        return true;
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error creating palette:', error);
        return false;
      }
    },

    /**
     * Delete a palette (cannot delete default)
     * @param {number} paletteId - ID of the palette to delete
     */
    async deletePalette (paletteId) {
      try {
        const palette = this.palettes.find(p => p.id === paletteId);
        if (!palette || palette.isDefault) {
          appDebugLog.warn('[ColorPalettes] Cannot delete default palette');
          return false;
        }

        await GeoMoverDB.deleteOne('colorPalettes', paletteId);
        await this.loadPalettes();
        return true;
      }
      catch (error) {
        appDebugLog.error('[ColorPalettes] Error deleting palette:', error);
        return false;
      }
    }
  }
});
